import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Gift, Image as ImageIcon, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function Prizes() {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsPrize, setDetailsPrize] = useState(null);

  useEffect(() => {
    api.get('/prizes')
      .then(res => setPrizes(res.data))
      .finally(() => setLoading(false));
  }, []);

  const statusColor = { 
    pendiente: 'bg-[#F4E39E]/20 text-[#F4E39E]', 
    asignado: 'bg-green-500/20 text-green-400', 
    anulado: 'bg-red-500/20 text-red-400' 
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="prizes-page">
      <div className="flex items-center gap-3 mb-6">
        <Gift size={32} className="text-[#F4E39E]" />
        <h2 className="text-2xl lg:text-3xl font-extrabold gold-text" style={{ fontFamily: 'Montserrat' }}>
          Premios
        </h2>
      </div>

      <p className="text-white/60 text-sm lg:text-base mb-6">
        Conoce los premios y regalos que se disputarán durante el torneo.
      </p>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando premios...</div>
      ) : prizes.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">
          No hay premios disponibles
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          {/* Header de tabla */}
          <div className="grid grid-cols-[2fr,1fr,1fr,1.5fr,auto] gap-4 p-4 bg-[#926F39]/10 border-b border-[#F4E39E]/20">
            <div className="text-[#F4E39E] font-bold text-sm">Premio</div>
            <div className="text-[#F4E39E] font-bold text-sm">Imagen</div>
            <div className="text-[#F4E39E] font-bold text-sm">Estado</div>
            <div className="text-[#F4E39E] font-bold text-sm">Usuario Ganador</div>
            <div className="text-[#F4E39E] font-bold text-sm text-right">Acciones</div>
          </div>

          {/* Filas de datos */}
          {prizes.map(prize => (
            <div 
              key={prize.id} 
              className="grid grid-cols-[2fr,1fr,1fr,1.5fr,auto] gap-4 p-4 border-b border-white/5 hover:bg-white/5 transition-colors items-center"
              data-testid={`prize-${prize.id}`}
            >
              {/* Premio */}
              <div>
                <div className="text-white font-semibold text-sm mb-1">{prize.name}</div>
                <div className="text-white/50 text-xs line-clamp-2">{prize.description}</div>
                {prize.stage && (
                  <div className="text-blue-400 text-xs mt-1">📍 {prize.stage.name}</div>
                )}
              </div>

              {/* Imagen */}
              <div>
                {prize.image_url ? (
                  <img 
                    src={prize.image_url} 
                    alt={prize.name}
                    className="w-16 h-16 object-cover rounded-lg border border-[#F4E39E]/30"
                  />
                ) : (
                  <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center">
                    <ImageIcon size={24} className="text-white/20" />
                  </div>
                )}
              </div>

              {/* Estado */}
              <div>
                <Badge className={statusColor[prize.status] || 'bg-white/10 text-white/50'}>
                  {prize.status}
                </Badge>
              </div>

              {/* Usuario Ganador */}
              <div>
                {prize.assigned_user ? (
                  <div className="text-green-400 text-xs">
                    <div className="font-semibold">{prize.assigned_user.user_id}</div>
                    {prize.assigned_user.name && (
                      <div className="text-white/50">{prize.assigned_user.name}</div>
                    )}
                  </div>
                ) : (
                  <span className="text-white/30 text-xs">Sin asignar</span>
                )}
              </div>

              {/* Acciones */}
              <div className="flex items-center justify-end">
                <button 
                  onClick={() => { setDetailsPrize(prize); setShowDetails(true); }} 
                  className="p-2 rounded hover:bg-white/10 text-white/50 hover:text-blue-400 transition-colors"
                  title="Ver detalles"
                  data-testid={`view-prize-${prize.id}`}
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Detalles del Premio */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>
              Detalles del Premio
            </DialogTitle>
          </DialogHeader>
          {detailsPrize && (
            <div className="space-y-4">
              {detailsPrize.image_url && (
                <div className="flex justify-center">
                  <img
                    src={detailsPrize.image_url}
                    alt={detailsPrize.name}
                    className="w-48 h-48 object-cover rounded-lg border-2 border-[#F4E39E]/30"
                  />
                </div>
              )}

              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Nombre</div>
                <div className="text-white font-semibold text-base break-words">{detailsPrize.name}</div>
              </div>

              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Descripción</div>
                <div className="text-white/90 text-sm break-words whitespace-pre-wrap max-h-40 overflow-y-auto pr-1">
                  {detailsPrize.description || <span className="text-white/30">Sin descripción</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Fase</div>
                  <div className="text-blue-400 text-sm">
                    {detailsPrize.stage?.name || <span className="text-white/30">Sin etapa</span>}
                  </div>
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Estado</div>
                  <Badge className={statusColor[detailsPrize.status] || 'bg-white/10 text-white/50'}>
                    {detailsPrize.status}
                  </Badge>
                </div>
              </div>

              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Usuario asignado</div>
                {detailsPrize.assigned_user ? (
                  <div className="text-green-400 text-sm">
                    <span className="font-semibold">{detailsPrize.assigned_user.user_id}</span>
                    {detailsPrize.assigned_user.name && (
                      <span className="text-white/60"> — {detailsPrize.assigned_user.name}</span>
                    )}
                  </div>
                ) : (
                  <div className="text-white/30 text-sm">Sin asignar</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
